import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { K } from '../kabinet/atoms';
import { Подложка } from '../ui/modal';
import { IconClose } from '../ui/icons';
import { форматЧисла } from '../ui/chisla';
import { ГРАНЬ } from '../celi-gran';
import { goalClass } from '../campaign/columns';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ЦЕЛИ ПО ДЕНЬГАМ — МЕТКА В ПОЛОСЕ ИТОГОВ И ОКНО ЗА НЕЙ.
 *
 * Эталон, дословно:
 *
 *     «Одному продавцу заказ за 500 ₽ выгоден, другому 100 ₽ уже дорого.
 *      Сервис не угадывает: он сравнивает с вашим пределом — от него
 *      зависят слова „работает“ и „сливает“ и цвет чисел.»
 *
 * До сих пор сервис угадывал: пороги жили числами в коде и были одни
 * на всех. Для продавца с наценкой в три конца они занижены вдвое,
 * для продавца с наценкой в двадцать процентов — завышены вдвое,
 * и в обоих случаях цвет числа врёт.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЦЕЛЬ НЕ ЗАДАНА — МЕТКА ЗОВЁТ ЕЁ ЗАДАТЬ, А НЕ МОЛЧИТ
 *
 * Умолчание у службы есть всегда (18 %, число эталона), и вердикты
 * считаются с первого дня. Но человек, который цель не задавал,
 * не знает, откуда взялись слова «сливает», — и метка говорит ему
 * об этом прямо, а не притворяется, что он сам так решил.
 * ═══════════════════════════════════════════════════════════════════════
 */

export type РежимЦены = 'from-drr' | 'own';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ЧЕМ МЕРИТЬ «ДАННЫХ ХВАТИЛО» — ДВОЙНИК СЛУЖЕБНОГО `ПорогРежим`.
 *
 * Клик — мера внимания, рубль — мера риска, и правы оба, но разным
 * людям. Довод целиком — в `src/rk/celi.ts`; числа умолчаний тоже
 * оттуда, и расходиться им нельзя: сторож `celi-porog.spec.ts`.
 * ═══════════════════════════════════════════════════════════════════════
 */
export type ПорогРежим = 'clicks' | 'spend';

/** Столько кликов ждёт кабинет, пока не сказано иначе (ответ К-117). */
export const МАЛО_КЛИКОВ = 30;

/** Столько же по деньгам. */
export const МАЛО_РУБЛЕЙ = 500;

export interface ЦелиСервера {
  задана: boolean;
  цели: {
    drr: number;
    cpoMode: РежимЦены;
    cpo: number | null;
    вПравила: boolean;
    порогРежим: ПорогРежим;
    порог: number;
  };
  ступени: Array<{ имя: string; предел: string }>;
  ценаЗаказа: number | null;
  история: {
    медиана: number | null;
    худший: number | null;
    среднийЧек: number | null;
    суток: number;
  };
  предложить: number | null;
}

/* Грань слива живёт голым числом в `web/src/celi-gran.ts` — довод там.
   Здесь она перевыставлена, чтобы читающие окно целей нашли её на месте. */
export { ГРАНЬ };

export function useЦели(base: string, включено = true) {
  return useQuery({
    queryKey: ['цели', base],
    queryFn: () => api<ЦелиСервера>(`${base}/goals`),
    /**
     * ЧИТАЕТСЯ НЕ ВСЕГДА, А ТОЛЬКО КОГДА НУЖНО.
     *
     * В биддере цели нужны сразу: метка «цель 18 %» стоит в полосе
     * итогов и должна назвать число. На карточке кабинета — нет:
     * там своё число уже пришло вместе со списком, а полная выкладка
     * (история, предложение) нужна только открытому окну. Три
     * карточки на экране выбора означали бы три обращения к базе
     * за историей, которую никто не смотрит.
     */
    enabled: base !== '' && включено,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * МЕТКА ЦЕЛИ ГОВОРИТ НЕ ТОЛЬКО ЧИСЛО, НО И ДОСТИГНУТА ЛИ ОНА.
 *
 * 01.09.2026 владелец: «цель, которая настраиваемая, нужно чтобы она
 * подсвечивалась в зависимости от достижения. Т.е. если достигнута —
 * зелёным, близко — оранжевым, ну и красным».
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ НУЖНО. Метка стоит вплотную к ДРР периода: «ДРР
 * 15,6 % · цель 10 %». Два числа рядом, и человек каждый раз считает
 * разницу в уме — при том что весь смысл предела в том, чтобы
 * не считать. Цвет отвечает на этот вопрос до чтения.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * СТУПЕНИ — ОБЩИЕ, А НЕ СВОИ. Красит `goalClass`: до предела — хорошо,
 * до полутора пределов (`ГРАНЬ`) — «близко», выше — слив. Тем же
 * множителем служба считает вердикты, им же подписано окно целей
 * («сливает — выше 27 %») и покрашен столбец ДРР в таблице.
 *
 * Завести здесь своё «близко» значило бы спорить цветом с тремя
 * местами сразу — и спорить там, где человек его не проверяет: метка
 * сказала бы «оранжевый», а строка под ней покраснела бы.
 *
 * ДРР НЕ СЧИТАЕТСЯ ЗДЕСЬ, А ПРИХОДИТ СВЕРХУ. Он уже посчитан полосой
 * итогов по ПОКАЗАННЫМ строкам — с учётом отбора и выделения. Считать
 * его второй раз значило бы завести второе число, которое разойдётся
 * с соседним в тот день, когда кто-нибудь включит фильтр.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function МеткаЦели({
  base,
  кабинет,
  достигнуто,
}: {
  base: string;
  кабинет: string;
  /**
   * ДРР за период по показанным строкам, % — то самое число, что стоит
   * слева от метки. `null`/не передан — красить нечем, метка остаётся
   * серой: цвет без числа был бы гаданием.
   */
  достигнуто?: number | null;
}) {
  const [открыто, setОткрыто] = useState(false);
  const q = useЦели(base);

  if (q.data === undefined) return null;

  const подпись = q.data.задана
    ? `цель ${q.data.цели.drr} %`
    : 'цель не задана';

  const тон = q.data.задана
    ? goalClass(достигнуто ?? null, q.data.цели.drr)
    : null;

  return (
    <>
      {/* ЦЕЛИ НЕТ — МЕТКА КРАСНЕЕТ. Так в эталоне (`--warn-wash` /
          `--warn-ink`), и по делу: «цель не задана» ровным серым
          читается как сведение, а это не сведение, а недоделка,
          из-за которой вся таблица отвечает «данных мало». */}
      <button
        type="button"
        data-d="метка-цели"
        className={q.data.задана ? K.меткаЦели : K.меткаЦелиНеЗадана}
        /* ═══════════════════════════════════════════════════════════
           ЦВЕТ ЗАДАЁТСЯ ЗДЕСЬ, А НЕ КЛАССОМ, И ЭТО ТОТ ЖЕ ДОВОД,
           ЧТО У ЗНАЧКА ТЕСТА СТАВКИ.

           `atoms.css` — зеркало эталона, его частицы правит сверка
           с `design/`. Новый класс в нём разошёлся бы с эталоном
           в первую же сверку. Форму и размер метки по-прежнему задаёт
           `k-метка-цели`; мы задаём только цвет состояния — и только
           когда состояние есть. */
        style={
          тон === null
            ? undefined
            : {
                background: `var(--${тон}-wash)`,
                color: `var(--${тон}-ink)`,
              }
        }
        title={
          тон === 'good'
            ? `Цель достигнута: ДРР ниже предела ${q.data.цели.drr} %. Нажмите, чтобы изменить`
            : тон === 'serious'
              ? `Цель не достигнута, но рядом: предел ${q.data.цели.drr} %, ` +
                `сливом считается выше ${Math.round(q.data.цели.drr * ГРАНЬ)} %. ` +
                'Нажмите, чтобы изменить'
              : тон === 'bad'
                ? `Цель не достигнута: ДРР выше ${Math.round(q.data.цели.drr * ГРАНЬ)} %, ` +
                  'это уже слив. Нажмите, чтобы изменить'
                : 'Цели по деньгам: от них считаются вердикты «работает» и «сливает»'
        }
        onClick={() => setОткрыто(true)}
      >
        {подпись}
      </button>
      {открыто && (
        <ОкноЦелей
          base={base}
          кабинет={кабинет}
          данные={q.data}
          закрыть={() => setОткрыто(false)}
        />
      )}
    </>
  );
}

/**
 * Принять предложенный предел одним нажатием.
 *
 * ТЕЛО — ПОЛНЫЕ ЦЕЛИ, А НЕ ОДИН `drr`. Служба принимает цели целиком
 * (`PUT /goals`), и послать половину значило бы стереть остальное:
 * способ цены заказа и «применять в правилах» вернулись бы
 * к умолчанию у человека, который их когда-то менял.
 */
function usePrinyat(base: string) {
  const qc = useQueryClient();
  const q = useЦели(base);
  return useMutation({
    mutationFn: (drr: number) =>
      api(`${base}/goals`, {
        method: 'PUT',
        body: {
          drr,
          cpoMode: q.data?.цели.cpoMode ?? 'from-drr',
          cpo: q.data?.цели.cpo ?? null,
          вПравила: q.data?.цели.вПравила ?? false,
          /* Порог достоверности идёт вместе с остальным: служба
             принимает цели целиком, и послать половину значило бы
             стереть то, чего не послали. */
          порогРежим: q.data?.цели.порогРежим ?? 'clicks',
          порог: q.data?.цели.порог ?? МАЛО_КЛИКОВ,
        },
      }),
    onSuccess: async () => {
      /* ═══════════════════════════════════════════════════════════════
         ЦЕЛИ МЕНЯЮТ НЕ ТОЛЬКО ЭТО ОКНО.

         От предела зависят вердикты в строках («работает» / «сливает»),
         сводка над таблицей, цвет столбцов ДРР и цены заказа, плитки
         сверху и разбор по товарам. Считает их СЛУЖБА, и все они уже
         лежат в памяти запросов, посчитанные по СТАРОМУ пределу.

         Обновляли мы здесь один ключ — `цели`. Владелец 28.08 задал
         предел, закрыл окно и написал: «ничего не произошло, вердикты
         остались теми же». Он был прав дважды: служба тогда и правда
         не спрашивала целей, но даже когда стала — экран показывал бы
         прежнее, пока человек не обновит страницу.

         Настройка, действующая только после перезагрузки, читается как
         недействующая, и спорить с этим нечем. */
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['цели', base] }),
        qc.invalidateQueries({ queryKey: ['campaigns'] }),
        qc.invalidateQueries({ queryKey: ['breakdown'] }),
        qc.invalidateQueries({ queryKey: ['overview'] }),
        qc.invalidateQueries({ queryKey: ['campaign-products'] }),
      ]);
    },
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПЛАШКА «ПОСТАВЬТЕ ПРЕДЕЛ ДРР» — ПЕРВЫМ, ЧТО ВИДИТ ГЛАЗ.
 *
 * Без предела ДРР сервис не отличает рабочую кампанию от сливающей:
 * вердикт у каждой строки — «данных мало», и таблица не отвечает
 * на вопрос, ради которого её открыли. Экран при этом выглядит
 * обычным, и человек может месяц смотреть в список, не зная,
 * что не хватает ОДНОГО числа.
 *
 * У эталона плашка стоит ВЫШЕ ЗАГОЛОВКА экрана (y = 109 при заголовке
 * на 223): не подсказкой сбоку, а первым, что читается.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЧИСЛА В НЕЙ — ИЗ ИСТОРИИ КАБИНЕТА, А НЕ ИЗ ГОЛОВЫ.
 *
 * «медиана 14 %, худший месяц 21 %, предлагаем 18 %» — всё это уже
 * считает служба (`src/rk/celi.ts`) и отдаёт вместе с целями. Своего
 * счёта здесь нет намеренно: второй такой же разошёлся бы с первым
 * ровно в тот день, когда правило подбора поменяют.
 *
 * ГРАНЬ СЛИВА — ТА ЖЕ, ЧТО В ОКНЕ ЦЕЛЕЙ (× 1.5). Два числа, названные
 * по-разному на соседних экранах, — это не мелочь: человек принимает
 * предел, глядя на «выше 27 %», и должен увидеть то же самое, открыв
 * настройку.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function ПлашкаЦели({ base, кабинет }: { base: string; кабинет: string }) {
  const [открыто, setОткрыто] = useState(false);
  const q = useЦели(base);
  const принять = usePrinyat(base);

  /* Плашки нет, когда цель задана — и когда предложить нечего:
     у кабинета без истории «предлагаем N %» взять неоткуда, а плашка
     без числа превратилась бы в упрёк без выхода. */
  if (q.data === undefined || q.data.задана || q.data.предложить === null) return null;

  const предел = q.data.предложить;
  const грань = Math.round(предел * ГРАНЬ);
  const и = q.data.история;

  return (
    <>
      <div data-d="плашка-цели" className={K.плашкаЦели}>
        <span className={K.столбикПлашкиЦели}>
          <b className={K.заголовокПлашкиЦели}>
            Поставьте предел ДРР — без него сервис не отличает рабочую кампанию от сливающей
          </b>
          <span className={K.подписьПлашкиЦели}>
            {и.медиана === null || и.худший === null
              ? `Предлагаем предел ${предел} %: всё, что выше ${грань} %, будем называть сливом.`
              : `По истории кабинета за ${и.суток} суток медиана ДРР — ` +
                `${дробью.format(и.медиана)} %, худший месяц — ${дробью.format(и.худший)} %. ` +
                `Предлагаем предел ${предел} %: ` +
                `всё, что выше ${грань} %, будем называть сливом.`}
          </span>
        </span>
        <span className={K.кнопкиПлашкиЦели}>
          <button
            type="button"
            data-d="кнопка-принять-цель"
            className={K.кнопкаПринятьЦель}
            disabled={принять.isPending}
            onClick={() => принять.mutate(предел)}
          >
            Принять {предел} %
          </button>
          {/* ВТОРАЯ КНОПКА ВЕДЁТ В ТО ЖЕ ОКНО, что и метка в полосе
              итогов: у согласного — одно нажатие, у несогласного —
              полная настройка, и обе двери на виду. */}
          <button
            type="button"
            className={K.кнопкаНастроитьЦель}
            onClick={() => setОткрыто(true)}
          >
            Настроить
          </button>
        </span>
      </div>
      {открыто && (
        <ОкноЦелей
          base={base}
          кабинет={кабинет}
          данные={q.data}
          закрыть={() => setОткрыто(false)}
        />
      )}
    </>
  );
}

/**
 * «Изменить» на карточке кабинета — та же дверь, что и метка в биддере.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ДВА ВХОДА, ОДНО ОКНО. В эталоне цели меняют из двух мест: меткой
 * «цель 18 %» в полосе итогов и словом «Изменить» на карточке кабинета
 * в экране выбора. Окно у них одно и то же — второе такое же однажды
 * разошлось бы с первым в том, что считает и что сохраняет.
 *
 * Разные здесь только вид кнопки и то, откуда берётся адрес кабинета:
 * в биддере он в адресной строке, на карточке — в самой карточке.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function ИзменитьЦели({
  base,
  кабинет,
  className,
}: {
  base: string;
  кабинет: string;
  className?: string;
}) {
  const [открыто, setОткрыто] = useState(false);
  /* ИСТОРИЯ ЧИТАЕТСЯ ПО НАЖАТИЮ, А НЕ ВМЕСТЕ С КАРТОЧКОЙ: число
     предела на карточке уже пришло со списком, а выкладка нужна
     только окну. */
  const q = useЦели(base, открыто);

  return (
    <>
      <button type="button" className={className} onClick={() => setОткрыто(true)}>
        Изменить
      </button>
      {открыто &&
        (q.data === undefined ? (
          /* ПОДЛОЖКА ПОЯВЛЯЕТСЯ СРАЗУ, а окно — когда прочитается.
             Иначе нажатие на полсекунды не отвечает ничем, и его
             повторяют. */
          <Подложка onClose={() => setОткрыто(false)} label="Цели по деньгам">
            <div data-d="окно-целей" className={K.окноЦелей}>
              <div className={K.телоОкнаЦелей}>
                <p className={K.поясненияЦелей}>
                  {q.isError ? 'Цели не прочитались — попробуйте ещё раз.' : 'Читаю цели…'}
                </p>
              </div>
            </div>
          </Подложка>
        ) : (
          <ОкноЦелей
            base={base}
            кабинет={кабинет}
            данные={q.data}
            закрыть={() => setОткрыто(false)}
          />
        ))}
    </>
  );
}

function ОкноЦелей({
  base,
  кабинет,
  данные,
  закрыть,
}: {
  base: string;
  кабинет: string;
  данные: ЦелиСервера;
  закрыть(): void;
}) {
  const qc = useQueryClient();
  const [drr, setDrr] = useState(String(данные.цели.drr));
  const [режим, setРежим] = useState<РежимЦены>(данные.цели.cpoMode);
  const [своё, setСвоё] = useState(данные.цели.cpo === null ? '' : String(данные.цели.cpo));
  const [вПравила, setВПравила] = useState(данные.цели.вПравила);
  const [порогРежим, setПорогРежим] = useState<ПорогРежим>(данные.цели.порогРежим);
  const [порог, setПорог] = useState(String(данные.цели.порог));

  /**
   * ПРИ СМЕНЕ РЕЖИМА ЧИСЛО ЗАМЕНЯЕТСЯ НА УМОЛЧАНИЕ ЭТОГО РЕЖИМА.
   *
   * Тридцать кликов и тридцать рублей — разные вещи: тридцать рублей
   * это ноль данных. Оставь число как есть при переключении, и человек,
   * нажавший «по расходу», получит порог, при котором вердикт выносится
   * практически сразу, — и не заметит этого.
   */
  const сменитьРежимПорога = (к: ПорогРежим) => {
    if (к === порогРежим) return;
    setПорогРежим(к);
    setПорог(String(к === 'spend' ? МАЛО_РУБЛЕЙ : МАЛО_КЛИКОВ));
  };
  const [беда, setБеда] = useState<string | null>(null);

  const число = Number(drr.replace(',', '.'));
  const годен = Number.isFinite(число) && число >= 1 && число <= 100;

  /* Ступени пересчитываются НА ГЛАЗАХ, пока человек набирает число.
     Иначе он видит последствия своего решения только после
     «Сохранить» — то есть тогда, когда менять уже поздно. */
  const предел = годен ? Math.round(число) : данные.цели.drr;
  const грань = Math.round(предел * ГРАНЬ);
  const ступени = [
    { имя: 'работает', предел: `до ${предел} %`, класс: K.ступеньЦели, имяКласс: K.имяСтупениРаботает },
    { имя: 'на грани', предел: `${предел}—${грань} %`, класс: K.ступеньЦелиГрань, имяКласс: K.имяСтупениГрань },
    { имя: 'сливает', предел: `выше ${грань} %`, класс: K.ступеньЦелиСливает, имяКласс: K.имяСтупениСливает },
  ];

  const изЧека =
    данные.история.среднийЧек === null
      ? null
      : Math.round((данные.история.среднийЧек * предел) / 100);

  const сохранить = useMutation({
    mutationFn: () =>
      api(`${base}/goals`, {
        method: 'PUT',
        // Тело передаётся объектом: `api` сам обращает его в строку
        // и сам ставит заголовок. Обратить здесь значило бы отправить
        // строку в строке
        body: {
          drr: Math.round(число),
          cpoMode: режим,
          cpo: режим === 'own' ? Number(своё.replace(',', '.')) : null,
          вПравила,
          порогРежим,
          порог: Math.round(Number(порог.replace(',', '.'))),
        },
      }),
    onSuccess: async () => {
      /* ═══════════════════════════════════════════════════════════════
         ЦЕЛИ МЕНЯЮТ НЕ ТОЛЬКО ЭТО ОКНО.

         От предела зависят вердикты в строках («работает» / «сливает»),
         сводка над таблицей, цвет столбцов ДРР и цены заказа, плитки
         сверху и разбор по товарам. Считает их СЛУЖБА, и все они уже
         лежат в памяти запросов, посчитанные по СТАРОМУ пределу.

         Обновляли мы здесь один ключ — `цели`. Владелец 28.08 задал
         предел, закрыл окно и написал: «ничего не произошло, вердикты
         остались теми же». Он был прав дважды: служба тогда и правда
         не спрашивала целей, но даже когда стала — экран показывал бы
         прежнее, пока человек не обновит страницу.

         Настройка, действующая только после перезагрузки, читается как
         недействующая, и спорить с этим нечем. */
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['цели', base] }),
        qc.invalidateQueries({ queryKey: ['campaigns'] }),
        qc.invalidateQueries({ queryKey: ['breakdown'] }),
        qc.invalidateQueries({ queryKey: ['overview'] }),
        qc.invalidateQueries({ queryKey: ['campaign-products'] }),
      ]);
      закрыть();
    },
    onError: (e: unknown) => setБеда(строкаОшибки(e)),
  });

  useEffect(() => setБеда(null), [drr, режим, своё]);

  return (
    <Подложка onClose={закрыть} label="Цели по деньгам">
      <div data-d="окно-целей" className={K.окноЦелей}>
        <div className={K.шапкаОкнаЦелей}>
          <b className={K.названиеОкнаЦелей}>Цели по деньгам</b>
          <span className={K.подписьОкнаЦелей}>кабинет {кабинет}</span>
          <button
            type="button"
            className={K.кнопкаЗакрытьЦели}
            aria-label="Закрыть"
            onClick={закрыть}
          >
            <IconClose size={14} />
          </button>
        </div>

        <div className={K.телоОкнаЦелей}>
          <p className={K.поясненияЦелей}>
            Одному продавцу заказ за 500 ₽ выгоден, другому 100 ₽ уже дорого. Сервис
            не угадывает: он сравнивает с вашим пределом — от него зависят слова
            «работает» и «сливает» и цвет чисел.
          </p>

          <div data-d="цель-дрр" className={K.блокЦели}>
            <span className={K.рядНазванияЦели}>
              <b className={K.названиеЦели}>Предел ДРР</b>
              <span className={K.подписьЦели}>
                сколько процентов выручки готовы отдавать рекламе
              </span>
            </span>

            <span className={K.рядПоляЦели}>
              <span className={K.уПоляЦели}>
                <input
                  data-d="поле-цели-дрр"
                  className={K.полеЦелиДрр}
                  inputMode="numeric"
                  value={drr}
                  onChange={(e) => setDrr(e.target.value)}
                  aria-label="Предел ДРР, проценты"
                />
                <b className={K.знакПоляЦели}>%</b>
              </span>

              {/* ПРЕДЛОЖЕННОЕ — КНОПКОЙ, А НЕ ПОДСТАВЛЕННЫМ ЗНАЧЕНИЕМ.
                  Подставленное человек принимает не глядя и потом
                  считает своим; нажатое — решение, которое он помнит. */}
              {данные.предложить !== null && (
                <button
                  type="button"
                  className={K.кнопкаВзятьПредложенное}
                  onClick={() => setDrr(String(данные.предложить))}
                >
                  Взять предложенное: {данные.предложить} %
                </button>
              )}
            </span>

            <span data-d="цель-из-истории" className={K.цельИзИстории}>
              {данные.история.медиана === null
                ? 'Истории по кабинету пока нет — предложить нечего, поставьте своё число.'
                : `По истории кабинета за ${данные.история.суток} суток: медиана ДРР — ` +
                  `${данные.история.медиана} %, худший месяц — ${данные.история.худший} %.`}
            </span>
          </div>

          <div data-d="ступени-цели" className={K.ступениЦели}>
            {ступени.map((с) => (
              <span key={с.имя} className={с.класс}>
                <b className={с.имяКласс}>{с.имя}</b>
                <span className={K.пределСтупени}>{с.предел}</span>
              </span>
            ))}
          </div>

          <div data-d="цель-заказа" className={K.блокЦеныЗаказа}>
            <span className={K.рядНазванияЦели}>
              <b className={K.названиеЦели}>Предел цены заказа</b>
              <span className={K.подписьЦели}>
                для запросов и площадок: выручки по ним Wildberries не отдаёт
              </span>
            </span>

            <span className={K.рядПоляЦели}>
              <span className={K.переключательЦены}>
                <button
                  type="button"
                  aria-pressed={режим === 'from-drr'}
                  className={режим === 'from-drr' ? K.кнопкаРежимаЦеныВыбрана : K.кнопкаРежимаЦены}
                  onClick={() => setРежим('from-drr')}
                >
                  Считать из ДРР
                </button>
                <button
                  type="button"
                  aria-pressed={режим === 'own'}
                  className={режим === 'own' ? K.кнопкаРежимаЦеныВыбрана : K.кнопкаРежимаЦены}
                  onClick={() => setРежим('own')}
                >
                  Своё число
                </button>
              </span>

              <span className={K.уПоляЦели}>
                <input
                  data-d="поле-цели-заказа"
                  className={K.полеЦелиЗаказа}
                  inputMode="numeric"
                  disabled={режим === 'from-drr'}
                  value={режим === 'from-drr' ? (изЧека === null ? '' : String(изЧека)) : своё}
                  onChange={(e) => setСвоё(e.target.value)}
                  aria-label="Предел цены заказа, рубли"
                />
                <b className={K.знакПоляЦели}>₽</b>
              </span>

              <span className={K.подписьРасчётаЦены}>
                {режим === 'own'
                  ? 'Своё число: сервис сравнивает цену заказа с ним, а не с расчётом.'
                  : данные.история.среднийЧек === null
                    ? 'Среднего чека по кабинету пока нет — считать не из чего.'
                    : `Считается из предела ДРР и среднего чека кабинета — ` +
                      `${рубли(данные.история.среднийЧек)} ₽: ${рубли(изЧека)} ₽`}
              </span>
            </span>
          </div>

          {/* ═══════════════════════════════════════════════════════════
              ПОРОГ ДОСТОВЕРНОСТИ — ТРЕТЬЯ НАСТРОЙКА, И ОНА БЫЛА
              ПОСТОЯННОЙ В КОДЕ.

              Внизу окна стояла строка «Пока кликов меньше 30, сервис
              пишет „данных мало"» — сведение о числе, которое человек
              не мог изменить. У продавца дорогой мебели тридцать кликов
              копятся неделю, у продавца носков — за час; и то, и другое
              «пока молчим», но сроки молчания разные на порядок.

              ДВА СПОСОБА СКАЗАТЬ ОДНО: клик — мера внимания, рубль —
              мера риска. Переключателем, как у цены заказа: два порога
              сразу означали бы, что человек держит в голове оба
              и гадает, какой сработал.

              Этого блока у эталона нет — он в списке «заводить»
              (ответ К-117). Слова и устройство наши, вид взят
              с соседнего блока цены заказа: то же окно, те же частицы.
              ═══════════════════════════════════════════════════════ */}
          <div data-d="порог-достоверности" className={K.блокЦеныЗаказа}>
            <span className={K.рядНазванияЦели}>
              <b className={K.названиеЦели}>Когда выносить вердикт</b>
              <span className={K.подписьЦели}>
                пока данных меньше — сервис молчит, а не гадает
              </span>
            </span>

            <span className={K.рядПоляЦели}>
              <span className={K.переключательЦены}>
                <button
                  type="button"
                  aria-pressed={порогРежим === 'clicks'}
                  className={
                    порогРежим === 'clicks' ? K.кнопкаРежимаЦеныВыбрана : K.кнопкаРежимаЦены
                  }
                  onClick={() => сменитьРежимПорога('clicks')}
                >
                  По кликам
                </button>
                <button
                  type="button"
                  aria-pressed={порогРежим === 'spend'}
                  className={
                    порогРежим === 'spend' ? K.кнопкаРежимаЦеныВыбрана : K.кнопкаРежимаЦены
                  }
                  onClick={() => сменитьРежимПорога('spend')}
                >
                  По расходу
                </button>
              </span>

              <span className={K.уПоляЦели}>
                <input
                  data-d="поле-порога"
                  className={K.полеЦелиЗаказа}
                  inputMode="numeric"
                  value={порог}
                  onChange={(e) => setПорог(e.target.value)}
                  aria-label={
                    порогРежим === 'spend'
                      ? 'Порог достоверности, рубли'
                      : 'Порог достоверности, клики'
                  }
                />
                <b className={K.знакПоляЦели}>{порогРежим === 'spend' ? '₽' : 'кл.'}</b>
              </span>

              <span className={K.подписьРасчётаЦены}>
                {порогРежим === 'spend'
                  ? 'Считаем по потраченному: у дорогого клика тридцать кликов — это уже большие деньги.'
                  : 'Считаем по кликам: тридцать человек посмотрели и решили — этого хватает для вердикта.'}
              </span>
            </span>
          </div>

          <div className={K.низОкнаЦелей}>
            {/* СТРОКА ГОВОРИТ ТО, ЧТО СТОИТ В ПОЛЕ ВЫШЕ, а не постоянную
                из кода. Слова считает служба (`порогСловами`), чтобы
                вердикт и объяснение к нему не разошлись. */}
            <span className={K.поясненияМалоКликов}>
              {порогРежим === 'spend'
                ? `Пока потрачено меньше ${порог || 0} ₽, сервис пишет «данных мало» и вердикта не выносит.`
                : `Пока кликов меньше ${порог || 0}, сервис пишет «данных мало» и вердикта не выносит.`}
            </span>
            <label className={K.галочкаВПравила}>
              <input
                type="checkbox"
                className={K.квадратикВПравила}
                checked={вПравила}
                onChange={(e) => setВПравила(e.target.checked)}
              />
              <span className={K.столбикГалочки}>
                {/* ═══════════════════════════════════════════════════
                    ЭТО ТО САМОЕ МЕСТО, ЧТО СВЯЗЫВАЕТ ПРЕДЕЛ КАБИНЕТА
                    С ПРАВИЛОМ «ДОРОГОЙ ЗАКАЗ» — П-8, 15.09.2026.

                    Галочка звалась «Брать этот предел в правила
                    остановки» и отправляла человека к разделу, которого
                    больше нет: правила остановки отменены, их место
                    заняли правила исключения на кампанию.

                    ИМЯ ПРАВИЛА НАЗВАНО ДОСЛОВНО, а не описано. «В правила
                    исключения» не сказало бы, в какое из семи: предел
                    заказа берёт ровно одно — «Дорогой заказ».
                    ═══════════════════════════════════════════════ */}
                <span className={K.названиеГалочки}>
                  Брать этот предел в правило «Дорогой заказ»
                </span>
                <span className={K.подписьГалочки}>
                  иначе порогов будет два: один красит числа, другой исключает запросы
                </span>
              </span>
            </label>
          </div>
        </div>

        <div className={K.подвалОкнаЦелей}>
          <span className={K.приписьПодвалаЦелей}>
            {беда ?? 'Цель одна на кабинет: у каждого кабинета своя экономика.'}
          </span>
          <button type="button" className={K.кнопкаОтменыЦелей} onClick={закрыть}>
            Отмена
          </button>
          <button
            type="button"
            data-d="кнопка-сохранить-цели"
            className={K.кнопкаСохранитьЦели}
            disabled={!годен || сохранить.isPending}
            onClick={() => сохранить.mutate()}
          >
            {сохранить.isPending ? 'Сохраняю…' : 'Сохранить цели'}
          </button>
        </div>
      </div>
    </Подложка>
  );
}

/**
 * Рубли с разрядами: «2 380», а не «2380».
 *
 * Тем же способом, что и всюду в кабинете, — узким неразрывным
 * пробелом. Число без разрядов читается на треть дольше, и это
 * та самая треть, за которую человек успевает потерять мысль.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * ДОЛЯ ПРОЦЕНТА — ЧЕРЕЗ ЗАПЯТУЮ, А НЕ ЧЕРЕЗ ТОЧКУ.
 *
 * 27.08 плашка «поставьте предел ДРР» вышла на живой кабинет и написала
 * «медиана ДРР — 12.9 %» — с ТОЧКОЙ, в строке, где рядом стоят
 * «99 690,91 ₽» и «524,09 ₽» с запятой. Число подставлялось в строку
 * шаблоном как есть, а JavaScript печатает дробь по-английски.
 *
 * На стенде этого было не увидеть: у эталона медиана 14 и худший 21 —
 * целые, и точке негде было взяться.
 * ═══════════════════════════════════════════════════════════════════════
 */
const дробью = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });

function рубли(n: number | null): string {
  return n === null ? '—' : форматЧисла().format(n);
}

function строкаОшибки(e: unknown): string {
  const m = (e as { message?: string } | null)?.message;
  return typeof m === 'string' && m !== '' ? m : 'Не удалось сохранить цели';
}
